import { useState, useRef, useEffect } from "react";
import * as knowledgeService from "V2.0/services/knowledgebaseService";

import Joi from "joi-browser";
import Form from "V2.0/components/Form";
import { message } from "antd";
import { addParagraph } from "../../service";

const MAX_DESCRIPTIONS = 30;
const WORD_LIMIT = 400;
const CHARACTER_LIMIT = 2500;
const TEXTAREA_HEIGHT = "220px";

const wordLimitRegex = new RegExp(`^\\s*(\\S+\\s+){0,${WORD_LIMIT}}\\S*$`);
const validationMessages = {
  language: {
    string: {
      regex: { base: `This section must contain no more than ${WORD_LIMIT} words` },
      max: `This section must contain no more than ${CHARACTER_LIMIT} characters`,
    },
  },
};

function descriptionKey(index) {
  return `description${index + 1}`;
}

function countWords(text) {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function buildInitialInputFields() {
  return Object.fromEntries(
    Array.from({ length: MAX_DESCRIPTIONS }, (_, i) => [descriptionKey(i), ""]),
  );
}

function buildSchema() {
  return Object.fromEntries(
    Array.from({ length: MAX_DESCRIPTIONS }, (_, i) => {
      let validator = Joi.string();
      if (i > 0) validator = validator.allow(null, "");
      validator = validator
        .max(CHARACTER_LIMIT)
        .regex(wordLimitRegex)
        .options(validationMessages)
        .label("this section");
      return [descriptionKey(i), validator];
    }),
  );
}

export function AddTextData({
  projects,
  activeProject,
  projectId,
  KB,
  reload,
  setReload,
  getData,
  refetch,
  onClose,
  kbExists,
}) {
  const [count, setCount] = useState(1);
  const [inputFields, setInputFields] = useState(buildInitialInputFields);
  const [errorMessage, setErrorMessage] = useState({});
  const [loading, setLoading] = useState(false);
  const ref = useRef({
    current: "",
  });

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollIntoView({ block: "end", behavior: "smooth" });
    }
  }, [count]);

  const template = Array.from({ length: count }, (_, i) => {
    const key = descriptionKey(i);
    const fieldError = errorMessage[key];
    return {
      name: key,
      label: fieldError
        ? `Text block ${i + 1} — ${fieldError}`
        : `Text block ${i + 1}`,
      placeholder: "Copy description here",
      type: "textArea",
      style: {
        height: TEXTAREA_HEIGHT,
        ...(fieldError && { border: "2px solid #ef4444" }),
      },
    };
  });

  const schema = buildSchema();

  const handleSubmit = async (data) => {
    const fieldErrors = {};
    Object.entries(data).forEach(([key, value]) => {
      if (!value) return;
      if (value.length > CHARACTER_LIMIT) {
        fieldErrors[key] = `This section must contain no more than ${CHARACTER_LIMIT} characters`;
      } else if (countWords(value) > WORD_LIMIT) {
        fieldErrors[key] = `This section must contain no more than ${WORD_LIMIT} words`;
      }
    });

    if (Object.keys(fieldErrors).length > 0) {
      setErrorMessage(fieldErrors);
      message.error(
        "One or more text blocks exceed the limit — check the highlighted field(s) below.",
      );
      return;
    }
    setErrorMessage({});

    const output = Object.values(data)
      .filter((value) => value !== "")
      .map((value) => ({ value: { data: value } }));

    if (kbExists) {
      const finalData = { paragraphs: output };

      try {
        const response = await addParagraph({
          projectId: projects[activeProject]._id,
          knowledgebaseId: KB._id,
          payload: finalData,
        });
        if (response.status === 201) {
          message.success("Paragraph added");
          setLoading(false);
          refetch(projects[activeProject]._id, KB._id);
          onClose();
        }
      } catch (e) {
        setLoading(false);
        message.error(
          e.response.data?.msg ||
            e.response.data?.message ||
            e.response.data?.error ||
            "Failed to add paragraph"
        );
        return;
      }

      return;
    }

    const finalData = {};
    finalData.label = projects[activeProject].name;
    finalData.projectId = projects[activeProject]._id;

    finalData.paragraphs = output;

    try {
      const response = await knowledgeService.createProject(finalData);
      if (response.status === 200) {
        message.success("Paragraph added");
        refetch(projects[activeProject]._id);
        onClose();
      }
    } catch (e) {
      setLoading(false);
      message.error(
        e.response.data?.msg ||
          e.response.data?.message ||
          e.response.data?.error ||
          "Failed to add paragraph"
      );
    }
  };

  const handleAddMore = () => {
    if (count === MAX_DESCRIPTIONS) {
      message.info(
        `You've reached the maximum of ${MAX_DESCRIPTIONS} text blocks — save this batch before adding more.`,
      );
      return;
    }
    setCount((prev) => prev + 1);
  };

  return (
    <div ref={ref} className="px-4">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Add text data</h2>
        <p className="mt-1 text-sm text-gray-500">
          Paste up to {MAX_DESCRIPTIONS} blocks of text — up to {WORD_LIMIT}{" "}
          words or {CHARACTER_LIMIT.toLocaleString()} characters each,
          whichever comes first. Use &ldquo;Add more&rdquo; for another
          block.
        </p>
        <p className="mt-1 text-xs text-gray-400">
          {count} of {MAX_DESCRIPTIONS} blocks used
        </p>
      </div>
      {Object.keys(errorMessage).length > 0 && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2">
          {Object.entries(errorMessage).map(([key, msg]) => {
            const blockNumber =
              parseInt(key.replace("description", ""), 10) || "?";
            return (
              <p
                key={key}
                className="text-sm text-red-600"
              >
                Text block {blockNumber}: {msg}
              </p>
            );
          })}
        </div>
      )}
      <Form
        className="p-4"
        errorMessage={errorMessage}
        inputField={inputFields}
        template={template}
        schema={schema}
        buttonInfo={{
          style: "",
          buttons: "2",
          primaryLabel: "Save",
          secondaryLabel: "Add more",
        }}
        onSubmit={handleSubmit}
        loading={loading}
        setLoading={setLoading}
        handleSecondary={handleAddMore}
        getValue={getData}
      />
    </div>
  );
}